import React, { useMemo } from 'react';
import { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import AlertComponent from '../../../../utility/AlertComponent';
import IAlertType from '../../../../../utils/types/props/IAlertType';
import ClientSelect from '../../../../select/ClientSelect';
import { IClient } from '../../../../../utils/types/IClient';
import TypeCarteSelect from '../../../../select/TypeCarteSelect';
import ArticleSelect from '../../../../select/ArticleSelect';
import BrancheSelect from '../../../../select/BrancheSelect';
import { IBranche } from '../../../../../utils/types/IBranche';
import StatutCarteSelect from '../../../../select/StatutCarteSelect';
import apiClient from '../../../../../services/api/apiClient';
import formatNumber from '../../../../../utils/formatters/formatNumber';
import { Carte } from '../../../../../utils/types/Carte';
import { format } from 'date-fns';
import { formatAxiosError } from '../../../../../utils/formatAxiosError';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../../../others/QueryErrorAlerts';
import { toast } from 'sonner';

//#region interfaces

interface BatchCartesModalProps {
    onSuccess?: () => void;
    setShowModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

//#endregion

const BatchCartesModalContent: React.FC<BatchCartesModalProps> = ({ onSuccess, setShowModal }) => {
    const queryClient = useQueryClient();

    // ---

    const {
        data: clients = [],
        isLoading: isLoadingClients,
        isFetching: isFetchingClients,
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    const {
        data: branches = [],
        isLoading: isLoadingBranches,
        isFetching: isFetchingBranches,
        refetch: refetchBranches,
        isError: isBranchesError,
        error: branchesError,
    } = useQuery<IBranche[]>({
        queryKey: ['branches'],
        queryFn: async () => {
            const res = await apiClient.get('/api/branches/getBranches');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des branches' },
        staleTime: 1000 * 60 * 60, 
    });

    // ---

    const { mutate, isPending: isSubmittingOperation } = useMutation({
        mutationFn: () =>
            apiClient.post('/api/cartes/batch', {
                cartes,
                cardCode: selectedCardCode,
            }),
        onSuccess: (response) => {
            if (response.data.success === true) {
                queryClient.invalidateQueries({ queryKey: ['cartes'] });

                Swal.fire({ text: 'Opération effectuée avec succès.', icon: 'success', confirmButtonText: 'OK' }).then(() => {
                    onSuccess?.();
                    setShowModal?.(false);
                });
            } else {
                setSystemAlerts((prev) => [...prev, { type: 'danger', message: response.data.message }]);
            }
        },
        onError: (err) => {
            addAlert('danger', formatAxiosError(err)?.message ?? 'Erreur lors de la création des cartes.');
        },
    });

    // ---

    //#region useState

    const [cartes, setCartes] = useState<Carte[]>([]);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    const [formState, setFormState] = useState<Carte>({
        codeClient: null,
        numero: '',
        type: '',
        description: '',
        idArticle: 0,
        dateValidite: new Date(),
        dateExtremeValidite: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
        idBranche: null,
        statut: 'Actif',
        limitePrise: null,
        limiteMensuelGo: null,
        limiteMensuelEs: null,
        rfidCode: '',
        codeQr: '',
        codePin: '',
        noLimitUpToMonthLimit: false,

        accessFailedCount: null,
        lockoutEnd: null,
        isLockedPermanently: null,

        // ---

        id: 0,
    });

    const [selectedCardCode, setSelectedCardCode] = useState<string | null>(null);
    const [selectedBrancheId, setSelectedBrancheId] = useState<number | null>(null);
    const [selectedTypeCarte, setSelectedTypeCarte] = useState<string | null>(null);
    const [selectedArticleId, setSelectedArticleId] = useState<number>(0);
    const [selectedStatut, setSelectedStatut] = useState<string | null>(null);

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);
    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };
    const addAlert = (type: IAlertType, message: string) => {
        setSystemAlerts((prevAlerts) => [...prevAlerts, { type, message }]);
    };

    //#endregion

    //#region methods

    const handleAddOrUpdateCarte = () => {
        const missingFields = [];
        if (!formState.numero) missingFields.push('Numéro');
        if (!formState.type) missingFields.push('Type');
        if (!formState.description) missingFields.push('Description');
        if (!formState.dateValidite) missingFields.push('Date de validité');
        if (!formState.dateExtremeValidite) missingFields.push('Date extrême de validité');
        if (!formState.statut) missingFields.push('Statut');
        if (!formState.rfidCode && !formState.codeQr) missingFields.push('Code RFID / QR');
        if (!formState.codePin) missingFields.push('Code PIN');

        if (missingFields.length > 0) {
            Swal.fire({
                text: `Veuillez remplir les champs obligatoires : ${missingFields.join(', ')}.`,
                icon: 'error',
                confirmButtonText: 'OK',
            });
            return;
        }

        const newCarte = { ...formState };

        if (editingIndex !== null) {
            // Modification
            setCartes((prev) => prev.map((carte, index) => (index === editingIndex ? newCarte : carte)));
            setEditingIndex(null);
        } else {
            // Nouvelle carte
            setCartes((prev) => [...prev, newCarte]);
        }

        resetForm();
    };

    const resetForm = () => {
        setFormState((prev) => ({
            ...prev,

            numero: '',
            porteur: '',
            idArticle: 0,
            rfidCode: '',
            codeQr: '',
            codePin: '',
        }));
    };

    const handleEditCarte = (index: number) => {
        const carte = cartes[index];
        setFormState(carte);
        setEditingIndex(index);
    };

    const handleDeleteCarte = (index: number) => {
        setCartes((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmitAll = () => {
        if (!selectedCardCode) {
            toast.warning('Client incorrect.');
            return;
        }

        mutate();
    };

    //#endregion

    //#region useEffect ===

    useEffect(() => {
        setFormState((prev) => ({
            ...prev,
            type: selectedTypeCarte,
        }));
    }, [selectedTypeCarte]);

    useEffect(() => {
        setFormState((prev) => ({
            ...prev,
            idArticle: selectedArticleId,
        }));
    }, [selectedArticleId]);

    useEffect(() => {
        setFormState((prev) => ({
            ...prev,
            idBranche: selectedBrancheId,
        }));
    }, [selectedBrancheId]);

    useEffect(() => {
        setFormState((prev) => ({
            ...prev,
            statut: selectedStatut,
        }));
    }, [selectedStatut]);

    //#endregion ===

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients },
            { isError: isBranchesError, error: branchesError, title: 'Erreur lors du chargement des branches', refetch: refetchBranches },
        ],
        [isClientsError, clientsError, refetchClients, isBranchesError, branchesError, refetchBranches],
    );

    // ---

    return (
        <>
            <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />
            <QueryErrorAlerts queries={queryErrors} />

            <div>
                <form action="#">
                    <div className="row">
                        <div className="col-md-6 form-group">
                            <label htmlFor="SelectedCardCode">Client *</label>
                            <ClientSelect clients={clients} isClientsLoading={isLoadingClients || isFetchingClients} selectedCardCode={selectedCardCode} setSelectedCardCode={setSelectedCardCode} />
                        </div>
                    </div>

                    <div className="hr-text">#</div>

                    <div className="card">
                        <div className="card-header">
                            <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs">
                                <li className="nav-item">
                                    <a href="#tabs-form" className="nav-link active" data-bs-toggle="tab">
                                        Formulaire
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a href="#tabs-data" className="nav-link" data-bs-toggle="tab">
                                        Données
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <div className="card-body">
                            <div className="tab-content">
                                <div className="tab-pane active show" id="tabs-form">
                                    <div className="row">
                                        <div className="col-md-6 form-group">
                                            <label htmlFor="numeroCarte">Numéro Carte *</label>
                                            <input
                                                type="text"
                                                id="numeroCarte"
                                                className="form-control"
                                                value={formState.numero ?? ''}
                                                placeholder="XXX XXX"
                                                onChange={(e) => {
                                                    let value = e.target.value.replace(/[^0-9]/g, '');
                                                    if (value.length > 6) value = value.slice(0, 6);

                                                    if (value.length > 3) {
                                                        value = value.slice(0, 3) + ' ' + value.slice(3);
                                                    }
                                                    setFormState((prev) => ({
                                                        ...prev,
                                                        numero: value,
                                                    }));
                                                }}
                                            />
                                        </div>

                                        <div className="col-md-6 form-group">
                                            <label>Type Carte *</label>
                                            <TypeCarteSelect setSelectedTypeCarte={setSelectedTypeCarte} />
                                        </div>

                                        <div className="col-md-6 form-group">
                                            <label>Porteur *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formState.description ?? ''}
                                                onChange={(e) =>
                                                    setFormState((prev) => ({
                                                        ...prev,
                                                        description: e.target.value,
                                                    }))
                                                }
                                            />
                                        </div>

                                        <div className="col-md-6 form-group">
                                            <label>Produit *</label>
                                            <ArticleSelect setSelectedArticle={setSelectedArticleId} selectedArticle={formState.idArticle} />
                                        </div>

                                        <div className="col-md-6 form-group">
                                            <label htmlFor="dateValidite">Date Validité *</label>
                                            <input
                                                type="date"
                                                id="dateValidite"
                                                className="form-control"
                                                value={formState.dateValidite instanceof Date ? formState.dateValidite.toISOString().split('T')[0] : ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setFormState((prev) => ({
                                                        ...prev,
                                                        dateValidite: value ? new Date(value) : null,
                                                    }));
                                                }}
                                            />
                                        </div>

                                        <div className="col-md-6 form-group">
                                            <label htmlFor="dateExtremeValidite">Date Extrême Validité *</label>
                                            <input
                                                type="date"
                                                id="dateExtremeValidite"
                                                className="form-control"
                                                value={formState.dateExtremeValidite instanceof Date ? formState.dateExtremeValidite.toISOString().split('T')[0] : ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setFormState((prev) => ({
                                                        ...prev,
                                                        dateExtremeValidite: value ? new Date(value) : null,
                                                    }));
                                                }}
                                            />
                                        </div>

                                        <div className="col-md-6 form-group">
                                            <label>Zone géographique</label>
                                            <BrancheSelect branches={branches} setSelectedBrancheId={setSelectedBrancheId} selectedBrancheId={selectedBrancheId} isBranchesLoading={isLoadingBranches || isFetchingBranches} />
                                        </div>

                                        <div className="col-md-6 form-group">
                                            <label>Statut *</label>
                                            <StatutCarteSelect setSelectedStatut={setSelectedStatut} selectedStatut={formState.statut} />
                                        </div>

                                        <div className="hr-text">#</div>

                                        <div className="col-md-4 form-group">
                                            <label htmlFor="limitePrise">Limite Prise</label>
                                            <input
                                                type="number"
                                                id="limitePrise"
                                                className="form-control"
                                                value={formState.limitePrise ?? ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const limitePrise = value === '' || isNaN(parseFloat(value)) ? null : parseFloat(value);

                                                    setFormState((prev) => ({
                                                        ...prev,
                                                        limitePrise: limitePrise,
                                                    }));
                                                }}
                                            />
                                        </div>

                                        <div className="col-md-4 form-group">
                                            <label htmlFor="limiteMensuelGo">Limite Mensulle Go</label>
                                            <input
                                                type="number"
                                                id="limiteMensuelGo"
                                                className="form-control"
                                                value={formState.limiteMensuelGo ?? ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const limiteMensuelGo = value === '' || isNaN(parseFloat(value)) ? null : parseFloat(value);

                                                    setFormState((prev) => ({
                                                        ...prev,
                                                        limiteMensuelGo: limiteMensuelGo,
                                                    }));
                                                }}
                                            />
                                        </div>

                                        <div className="col-md-4 form-group">
                                            <label htmlFor="limiteMensuelEs">Limite Mensulle ES</label>
                                            <input
                                                type="number"
                                                id="limiteMensuelEs"
                                                className="form-control"
                                                value={formState.limiteMensuelEs ?? ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const limiteMensuelEs = value === '' || isNaN(parseFloat(value)) ? null : parseFloat(value);

                                                    setFormState((prev) => ({
                                                        ...prev,
                                                        limiteMensuelEs: limiteMensuelEs,
                                                    }));
                                                }}
                                            />
                                        </div>

                                        <div className="col-md-4 form-group">
                                            <label htmlFor="codeRfid">Code RFID *</label>
                                            <input
                                                type="text"
                                                id="codeRfid"
                                                className="form-control"
                                                value={formState.rfidCode ?? ''}
                                                onChange={(e) =>
                                                    setFormState((prev) => ({
                                                        ...prev,
                                                        rfidCode: e.target.value,
                                                    }))
                                                }
                                            />
                                        </div>

                                        <div className="col-md-4 form-group">
                                            <label htmlFor="codeQr">Code QR *</label>
                                            <input
                                                type="text"
                                                id="codeQr"
                                                className="form-control"
                                                value={formState.codeQr ?? ''}
                                                onChange={(e) =>
                                                    setFormState((prev) => ({
                                                        ...prev,
                                                        codeQr: e.target.value,
                                                    }))
                                                }
                                            />
                                        </div>

                                        <div className="col-md-4 form-group">
                                            <label htmlFor="codePin">Code PIN *</label>
                                            <input
                                                type="text"
                                                id="codePin"
                                                className="form-control"
                                                value={formState.codePin ?? ''}
                                                onChange={(e) =>
                                                    setFormState((prev) => ({
                                                        ...prev,
                                                        codePin: e.target.value,
                                                    }))
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className="text-end mt-3">
                                        <button type="button" className="btn btn-outline-primary" onClick={handleAddOrUpdateCarte}>
                                            {editingIndex !== null ? 'Modifier' : 'Ajouter'}
                                        </button>
                                    </div>
                                </div>

                                <div className="tab-pane" id="tabs-data">
                                    {cartes.length > 0 ? (
                                        <>
                                            <div className="table-responsive">
                                                <table className="table table-hover table-nowrap table-bordered table-striped table-sm">
                                                    <thead>
                                                        <th>Numéro</th>
                                                        <th>Type</th>
                                                        <th>Porteur</th>
                                                        <th>Article</th>
                                                        <th>Date Validité</th>
                                                        <th>Date Extr. Validité</th>
                                                        <th>Zone géo.</th>
                                                        <th>Statut</th>
                                                        <th>Limite Prise</th>
                                                        <th>Lim. Mens. GO</th>
                                                        <th>Lim. Mens. ES</th>
                                                        <th>Code RFID</th>
                                                        <th>Code QR</th>
                                                        <th>Code PIN</th>
                                                        <th>Livraison sans recharge</th>
                                                        <th></th>
                                                    </thead>
                                                    <tbody>
                                                        {cartes.map((carte, index) => (
                                                            <tr key={index}>
                                                                <td>{carte.numero}</td>
                                                                <td>{carte.type}</td>
                                                                <td>{carte.description}</td>
                                                                <td>
                                                                    {{
                                                                        0: 'Multi-produit',
                                                                        1: 'Gasoil',
                                                                        2: 'Essence',
                                                                    }[carte.idArticle ?? 0] || ''}
                                                                </td>
                                                                <td>{carte.dateValidite && format(carte.dateValidite, 'dd/MM/yyyy')}</td>
                                                                <td>{carte.dateExtremeValidite && format(carte.dateExtremeValidite, 'dd/MM/yyyy')}</td>
                                                                <td>{branches.find((branche) => branche.id === carte.idBranche)?.nom || ''}</td>
                                                                <td>{carte.statut}</td>
                                                                <td className="text-end">{carte.limitePrise && formatNumber(carte.limitePrise)}</td>
                                                                <td className="text-end">{carte.limiteMensuelGo && formatNumber(carte.limiteMensuelGo)}</td>
                                                                <td className="text-end">{carte.limiteMensuelEs && formatNumber(carte.limiteMensuelEs)}</td>
                                                                <td>{carte.rfidCode}</td>
                                                                <td>{carte.codeQr}</td>
                                                                <td>{carte.codePin}</td>
                                                                <td className="text-center">
                                                                    <input type="checkbox" checked={carte.noLimitUpToMonthLimit ?? false} />
                                                                </td>

                                                                <td>
                                                                    <button type="button" onClick={() => handleEditCarte(index)} className="btn btn-sm btn-warning">
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-edit">
                                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                                            <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                                                                            <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                                                                            <path d="M16 5l3 3" />
                                                                        </svg>
                                                                    </button>
                                                                    &nbsp;|&nbsp;
                                                                    <button type="button" onClick={() => handleDeleteCarte(index)} className="btn btn-sm btn-danger">
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-trash">
                                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                                            <path d="M4 7l16 0" />
                                                                            <path d="M10 11l0 6" />
                                                                            <path d="M14 11l0 6" />
                                                                            <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                                                                            <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                                                                        </svg>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            <div className="text-end mt-3">
                                                <button
                                                    type="button"
                                                    className="btn btn-success"
                                                    onClick={() => {
                                                        handleSubmitAll();
                                                    }}
                                                    disabled={cartes.length === 0 || isSubmittingOperation}
                                                >
                                                    {isSubmittingOperation ? (
                                                        <>
                                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                            &nbsp; Valider
                                                        </>
                                                    ) : (
                                                        `Valider toutes les cartes (${cartes.length})`
                                                    )}
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="alert alert-warning mb-0" role="alert">
                                            <div className="alert-icon">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                                                    <path d="M12 9v4"></path>
                                                    <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                                                    <path d="M12 16h.01"></path>
                                                </svg>
                                            </div>
                                            Aucune donnée.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

const BatchCartesModal: React.FC<BatchCartesModalProps> = () => {
    //#region useState

    // State to control modal visibility
    const [showModal, setShowModal] = useState(false);

    // Functions to toggle the modal

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    //#endregion

    // ---

    return (
        <div>
            <Button className="btn btn-primary btn-5" onClick={handleOpenModal}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-2">
                    <path d="M12 5l0 14"></path>
                    <path d="M5 12l14 0"></path>
                </svg>
                Batch
            </Button>

            {/* Modal component */}
            <Modal show={showModal} onHide={handleCloseModal} className="fade" scrollable backdrop="static" size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Nouvelle cartes</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <BatchCartesModalContent setShowModal={setShowModal} />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default BatchCartesModal;
